// src/components/shared/MyBorrowedBooks.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { libraryAPI } from '../../services/api';

const MyBorrowedBooks = () => {
  const navigate = useNavigate();
  const [issues, setIssues] = useState([]);
  const [fines, setFines] = useState([]);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [issData, fineData] = await Promise.all([
        libraryAPI.getMyIssues().catch(() => []),
        libraryAPI.getMyFines().catch(() => []),
      ]);
      const issArr = Array.isArray(issData) ? issData : (issData?.results || []);
      const fineArr = Array.isArray(fineData) ? fineData : (fineData?.results || []);
      setIssues(issArr);
      setFines(fineArr);
      try {
        const mem = await libraryAPI.getMyMembership();
        setMembership(mem);
      } catch (_) {}
    } catch (e) {
      setError('Failed to load library data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const getDaysInfo = (issue) => {
    const due = new Date(issue.due_date);
    due.setHours(0, 0, 0, 0);
    const diff = Math.round((due - today) / 86400000);
    if (issue.status === 'RETURNED') return { label: 'Returned', color: '#6b7280', days: 0 };
    if (diff < 0) return { label: `${Math.abs(diff)} days overdue`, color: '#dc2626', days: diff };
    if (diff === 0) return { label: 'Due Today!', color: '#f59e0b', days: 0 };
    if (diff <= 3) return { label: `${diff} days left`, color: '#f59e0b', days: diff };
    return { label: `${diff} days left`, color: '#10b981', days: diff };
  };

  const getFineForIssue = (issueId) => fines.find(f => f.issue === issueId);

  const activeIssues = issues.filter(i => i.status === 'ISSUED');
  const returnedIssues = issues.filter(i => i.status === 'RETURNED');
  const overdueCount = activeIssues.filter(i => {
    const due = new Date(i.due_date);
    due.setHours(0, 0, 0, 0);
    return due < today;
  }).length;
  const totalFines = fines.filter(f => f.status === 'PENDING').reduce((s, f) => s + parseFloat(f.amount || 0), 0);

  if (loading) return (
    <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
      🔄 Loading your library records...
    </div>
  );

  return (
    <div style={{ fontFamily: "'Inter', sans-serif" }}>
      {error && (
        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '12px 16px', color: '#991b1b', marginBottom: '16px', fontSize: '14px' }}>
          {error}
        </div>
      )}

      {/* Digital Library Portal Access Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
        borderRadius: '12px',
        padding: '20px',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px',
        marginBottom: '24px',
        boxShadow: '0 4px 15px rgba(124, 58, 237, 0.25)'
      }}>
        <div style={{ flex: '1', minWidth: '280px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', letterSpacing: '-0.02em', color: '#ffffff' }}>📱 Access Digital Library Portal</h3>
          <p style={{ margin: '4px 0 0 0', fontSize: '13px', opacity: 0.9, color: '#ffffff' }}>Browse E-Books, download PDFs, view medical journals, and search the library catalog online.</p>
        </div>
        <button 
          onClick={() => navigate('/library-dashboard')}
          style={{
            background: 'white',
            color: '#4f46e5',
            border: 'none',
            borderRadius: '8px',
            padding: '10px 18px',
            fontWeight: '700',
            fontSize: '13px',
            cursor: 'pointer',
            boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
            transition: 'transform 0.15s ease'
          }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          Open Portal →
        </button>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '24px' }}>
        {[
          { label: 'Books Borrowed', value: activeIssues.length, color: '#3b82f6', icon: '📚' },
          { label: 'Overdue Books', value: overdueCount, color: overdueCount > 0 ? '#dc2626' : '#10b981', icon: '⏰' },
          { label: 'Outstanding Fines', value: `₹${totalFines.toFixed(2)}`, color: totalFines > 0 ? '#f59e0b' : '#10b981', icon: '💰' },
          { label: 'Return History', value: returnedIssues.length, color: '#8b5cf6', icon: '✅' },
        ].map((c, i) => (
          <div key={i} style={{ background: 'white', border: `2px solid ${c.color}20`, borderRadius: '12px', padding: '16px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: '28px', marginBottom: '6px' }}>{c.icon}</div>
            <div style={{ fontSize: '22px', fontWeight: '700', color: c.color }}>{c.value}</div>
            <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Membership Info */}
      {membership && (
        <div style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', fontSize: '13px' }}>
          <span>🪪 <strong>Member ID:</strong> {membership.membership_id}</span>
          <span>📋 <strong>Status:</strong> <span style={{ color: membership.status === 'ACTIVE' ? '#059669' : '#dc2626', fontWeight: '600' }}>{membership.status}</span></span>
          <span>📖 <strong>Quota:</strong> {membership.books_currently_issued}/{membership.max_books_allowed} books</span>
          {membership.membership_expiry && <span>📅 <strong>Expires:</strong> {new Date(membership.membership_expiry).toLocaleDateString()}</span>}
        </div>
      )}

      {/* Active Borrowed Books */}
      <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1f2937', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        📖 Currently Borrowed ({activeIssues.length})
      </h3>
      {activeIssues.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af', background: '#f9fafb', borderRadius: '10px', marginBottom: '24px' }}>
          No books currently borrowed. Visit the library to borrow a book!
        </div>
      ) : (
        <div style={{ overflowX: 'auto', marginBottom: '28px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Book Title', 'Copy Barcode', 'Issue Date', 'Due Date', 'Days Status', 'Fine', 'Fine Status'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {activeIssues.map((issue, idx) => {
                const daysInfo = getDaysInfo(issue);
                const fine = getFineForIssue(issue.id);
                const isOverdue = daysInfo.days < 0;
                return (
                  <tr key={issue.id} style={{ background: idx % 2 === 0 ? 'white' : '#fafafa', borderLeft: isOverdue ? '4px solid #dc2626' : daysInfo.days <= 3 && daysInfo.days >= 0 ? '4px solid #f59e0b' : '4px solid transparent' }}>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
                      <div style={{ fontWeight: '600', color: '#111827' }}>{issue.book_title}</div>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>Acc: {issue.book_accession}</div>
                    </td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', fontFamily: 'monospace', color: '#4b5563', fontSize: '12px' }}>{issue.copy_barcode}</td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' }}>{new Date(issue.issue_date).toLocaleDateString('en-IN')}</td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap', fontWeight: isOverdue ? '700' : '400', color: isOverdue ? '#dc2626' : 'inherit' }}>
                      {new Date(issue.due_date).toLocaleDateString('en-IN')}
                    </td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
                      <span style={{ background: `${daysInfo.color}20`, color: daysInfo.color, padding: '3px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                        {isOverdue ? '🔴 ' : daysInfo.days <= 3 ? '🟡 ' : '🟢 '}{daysInfo.label}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', fontWeight: '600', color: fine ? '#dc2626' : '#6b7280' }}>
                      {fine ? `₹${parseFloat(fine.amount).toFixed(2)}` : (isOverdue ? 'Accumulating' : '—')}
                    </td>
                    <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
                      {fine ? (
                        <span style={{ background: fine.status === 'PAID' ? '#dcfce7' : '#fee2e2', color: fine.status === 'PAID' ? '#166534' : '#991b1b', padding: '3px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                          {fine.status}
                        </span>
                      ) : <span style={{ color: '#6b7280', fontSize: '12px' }}>No Fine</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Return History */}
      {returnedIssues.length > 0 && (
        <>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1f2937', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            ✅ Return History ({returnedIssues.length})
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Book Title', 'Copy Barcode', 'Issue Date', 'Due Date', 'Returned On', 'Fine', 'Status'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {returnedIssues.map((issue, idx) => {
                  const fine = getFineForIssue(issue.id);
                  return (
                    <tr key={issue.id} style={{ background: idx % 2 === 0 ? 'white' : '#fafafa' }}>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
                        <div style={{ fontWeight: '500', color: '#374151' }}>{issue.book_title}</div>
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', fontFamily: 'monospace', fontSize: '12px', color: '#6b7280' }}>{issue.copy_barcode}</td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' }}>{new Date(issue.issue_date).toLocaleDateString('en-IN')}</td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' }}>{new Date(issue.due_date).toLocaleDateString('en-IN')}</td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap', color: '#059669', fontWeight: '500' }}>
                        {issue.return_date ? new Date(issue.return_date).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', color: fine ? '#dc2626' : '#6b7280' }}>
                        {fine ? `₹${parseFloat(fine.amount).toFixed(2)}` : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
                        <span style={{ background: '#dcfce7', color: '#166534', padding: '3px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>RETURNED</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default MyBorrowedBooks;
